import React, { useRef, useState } from 'react';
import { Upload, RefreshCw, Trash2, Info, Send } from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassButton } from '../ui/GlassButton';
import { GlassBadge } from '../ui/GlassBadge';
import { BrandingCapabilityBanner } from './BrandingCapabilityBanner';
import {
    BrandingDraft,
    BrandingDraftResponse,
    createBrandingDraft,
    updateBrandingDraft,
    regenerateBrandingCover,
    discardBrandingDraft,
    pushBrandingDraft,
    refreshBrandingDraftContact,
    uploadBrandAsset,
} from '../../services/api';
import { describeFieldResult } from './brandingResultCopy';
import { formatHoursSummary } from '../ui/BusinessHoursInput';
import './FacebookBrandingModal.css';

type PushField = 'picture' | 'cover' | 'about' | 'phone' | 'emails' | 'location' | 'hours' | 'website';

// Phase 5C: `location` (Page.location, the structured address) is now
// offered — see specs/completed/facebook-page-branding-live-push.md
// "Phase 5C". Its checkbox is disabled below when `address_pushable` is
// false, mirroring the spec's four blocked-state messages in the Address
// row rather than silently omitting the field. `hours` (Page.hours,
// structured business hours) was added in Phase 5/6 of
// specs/completed/fb-branding-address-hours-fix.md — same disabled-not-
// omitted treatment, keyed off contact_hours_pushable.
const PUSH_FIELD_DEFS: Array<{ key: PushField; label: string }> = [
    { key: 'picture', label: 'Profile picture' },
    { key: 'cover', label: 'Cover photo' },
    { key: 'about', label: 'About & Description' },
    { key: 'phone', label: 'Phone number' },
    { key: 'emails', label: 'Contact email' },
    { key: 'location', label: 'Address' },
    { key: 'hours', label: 'Business hours' },
    { key: 'website', label: 'Website' },
];

// Hours block blocked-state copy — keyed by the draft SNAPSHOT's
// contact_hours_block_reason (business_hours's reason vocabulary), computed
// server-side from draft.contact_hours. Mirrors ADDRESS_BLOCKED_MESSAGE.
const HOURS_BLOCKED_MESSAGE: Record<string, string> = {
    skipped_invalid_hours: 'The schedule in Project Settings has an error — fix it there.',
    skipped_no_source: 'Every day is marked closed — nothing to send.',
};

const GRAPH_HOURS_DAY_ORDER: Array<['mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', string]> = [
    ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun'],
];

// Formats `page_current.hours` — Graph's raw {day}_{n}_{open|close} map —
// into the same display shape formatHoursSummary produces for the AIME-side
// storage shape. Deliberately a separate, display-only parser (the backend's
// business_hours.format_summary accepts either shape; duplicating this much
// is acceptable per that function's own docstring).
function formatGraphHoursSummary(raw: Record<string, any> | null | undefined): string[] {
    if (!raw || Object.keys(raw).length === 0) return [];
    const perDay: Record<string, string> = {};
    for (const [key, label] of GRAPH_HOURS_DAY_ORDER) {
        const parts: string[] = [];
        for (const n of [1, 2]) {
            const open = raw[`${key}_${n}_open`];
            const close = raw[`${key}_${n}_close`];
            if (typeof open === 'string' && typeof close === 'string') parts.push(`${open}-${close}`);
        }
        perDay[label] = parts.length > 0 ? parts.join(', ') : 'Closed';
    }
    const lines: string[] = [];
    const labels = GRAPH_HOURS_DAY_ORDER.map(([, label]) => label);
    let i = 0;
    while (i < labels.length) {
        const start = i;
        const value = perDay[labels[i]];
        while (i + 1 < labels.length && perDay[labels[i + 1]] === value) i += 1;
        const end = i;
        const rangeLabel = start === end ? labels[start] : `${labels[start]}-${labels[end]}`;
        lines.push(`${rangeLabel} ${value}`);
        i += 1;
    }
    return lines;
}

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'default'> = {
    draft: 'info',
    pending_approval: 'warning',
    pushing: 'info',
    applied: 'success',
    partial: 'warning',
    failed: 'danger',
    discarded: 'default',
    expired: 'default',
    no_op: 'default',
};

interface FacebookBrandingModalProps {
    isOpen: boolean;
    accountId: string;
    accountLabel: string;
    draftData: BrandingDraftResponse | null;
    onClose: () => void;
    onDraftUpdated: (data: BrandingDraftResponse) => void;
    onReconnect: () => void;
}

const PROVENANCE_LABEL: Record<string, string> = {
    existing_brand_data: 'From your Brand Guide',
    gemini: 'AI-written',
    fallback: 'AI-written (fallback — edit before this can be pushed)',
    user_edited: 'Edited by you',
};

const SOURCE_LABEL: Record<string, string> = {
    draft_upload: 'Logo mark you uploaded',
    brand_logo_mark: 'Your logo mark (from Brand Guide)',
    gemini: 'AI-generated — no logo mark on file',
    none: 'No image available',
};

// Cover-photo logo overlay variant picker — mirrors ProjectSettings.tsx's
// carousel_logo_variant options (same underlying BrandGuide fields, plus
// logo_mark for the Phase 1A mark slot this feature added).
const COVER_LOGO_VARIANT_OPTIONS: Array<{ value: NonNullable<BrandingDraft['cover_logo_variant']>; label: string }> = [
    { value: 'logo_mark', label: 'Logo mark' },
    { value: 'primary', label: 'Primary logo' },
    { value: 'dark_bg', label: 'For dark backgrounds' },
    { value: 'monochrome_white', label: 'Monochrome (white)' },
    { value: 'monochrome_black', label: 'Monochrome (black)' },
];

// Address row blocked-state copy — keyed by the draft SNAPSHOT's
// contact_address_block_reason (address_structuring's reason vocabulary),
// computed server-side from draft.contact_address, never from live
// project_contact. See specs/completed/fb-branding-address-hours-fix.md F2.
const ADDRESS_BLOCKED_MESSAGE: Record<string, string> = {
    skipped_incomplete_address:
        'This is a suburb or area, not a street address — Facebook needs a street. '
        + 'Pick a street address in Project Settings.',
    skipped_unstructured_address:
        "This address was typed rather than picked from suggestions, so Facebook can't map it. "
        + 'Pick it from the suggestions in Project Settings to enable this.',
    skipped_address_unconfirmed: "This address hasn't been confirmed yet — confirm it in Project Settings.",
};

/**
 * The draft review + push modal for Facebook Page Branding (Phase 3: the
 * Push button is live; Phase 5C adds `location`, the structured address).
 * A push writes real Page metadata to Meta's Graph API — the confirm()
 * dialog in handlePush names the exact Page and the exact fields ticked,
 * and is deliberately not skippable via a "don't ask again" toggle.
 * `location`'s checkbox renders disabled whenever
 * project_contact.address_pushable is false — see ADDRESS_BLOCKED_MESSAGE.
 * See specs/completed/facebook-page-branding-live-push.md "New Components".
 */
export const FacebookBrandingModal: React.FC<FacebookBrandingModalProps> = ({
    isOpen,
    accountId,
    accountLabel,
    draftData,
    onClose,
    onDraftUpdated,
    onReconnect,
}) => {
    const [bioText, setBioText] = useState('');
    const [aboutLongText, setAboutLongText] = useState('');
    const [coverGuidance, setCoverGuidance] = useState('');
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [regeneratingCover, setRegeneratingCover] = useState(false);
    const [applyingOverlay, setApplyingOverlay] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [discarding, setDiscarding] = useState(false);
    const [pushing, setPushing] = useState(false);
    const [refreshingContact, setRefreshingContact] = useState(false);
    const [selectedFields, setSelectedFields] = useState<Set<PushField>>(
        new Set(PUSH_FIELD_DEFS.map((f) => f.key)),
    );
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const draft: BrandingDraft | null = draftData?.draft || null;

    React.useEffect(() => {
        if (draft) {
            setBioText(draft.bio_text || '');
            setAboutLongText(draft.about_long_text || '');
            setCoverGuidance(draft.banner_prompt_guidance || '');
        }
    }, [draft?.draft_id, draft?.bio_text, draft?.about_long_text, draft?.banner_prompt_guidance]);

    // Reset the field checkboxes to "everything" whenever a new draft loads
    // (regenerate, or opening a different account) — a stale selection from
    // a previous draft must never silently carry over. `location` is
    // excluded from the default selection when the address isn't pushable
    // (Phase 5C) — its checkbox renders disabled below, so it should never
    // start checked either. `hours` got the same live-verification gate
    // (Phase 7 of fb-branding-address-hours-fix.md) but passed and joined
    // the defaults — no special-casing needed here, same as `location` once
    // it's pushable.
    React.useEffect(() => {
        const addressIsPushable = !!draft?.contact_address_pushable;
        const hoursIsPushable = !!draft?.contact_hours_pushable;
        setSelectedFields(
            new Set(
                PUSH_FIELD_DEFS
                    .filter((f) => f.key !== 'location' || addressIsPushable)
                    .filter((f) => f.key !== 'hours' || hoursIsPushable)
                    .map((f) => f.key),
            ),
        );
    }, [draft?.draft_id, draft?.contact_address_pushable, draft?.contact_hours_pushable]);

    const getApiError = (e: any, fallback: string): string => {
        const detail = e?.response?.data?.detail;
        if (!detail) return fallback;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
        return fallback;
    };

    const handleSaveBio = async () => {
        if (!draft) return;
        setSaving(true);
        setError(null);
        try {
            const result = await updateBrandingDraft(draft.draft_id, {
                bio_text: bioText,
                about_long_text: aboutLongText,
            });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to save bio'));
        } finally {
            setSaving(false);
        }
    };

    const handleUseAiBio = async () => {
        if (!draft) return;
        setSaving(true);
        setError(null);
        try {
            const result = await updateBrandingDraft(draft.draft_id, {
                bio_text: draft.bio_text_generated || '',
                bio_source: 'gemini',
            });
            setBioText(result.draft.bio_text || '');
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to switch to AI bio'));
        } finally {
            setSaving(false);
        }
    };

    const handleToggleReplaceDescription = async (checked: boolean) => {
        if (!draft) return;
        try {
            const result = await updateBrandingDraft(draft.draft_id, { replace_page_description: checked });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to update setting'));
        }
    };

    const handleToggleReplaceEmails = async (checked: boolean) => {
        if (!draft) return;
        try {
            const result = await updateBrandingDraft(draft.draft_id, { replace_page_emails: checked });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to update setting'));
        }
    };

    const handleToggleReplaceLocation = async (checked: boolean) => {
        if (!draft) return;
        try {
            const result = await updateBrandingDraft(draft.draft_id, { replace_page_location: checked });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to update setting'));
        }
    };

    const handleToggleReplaceHours = async (checked: boolean) => {
        if (!draft) return;
        try {
            const result = await updateBrandingDraft(draft.draft_id, { replace_page_hours: checked });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to update setting'));
        }
    };

    const handleUploadLogoMark = async (file: File) => {
        if (!draft) return;
        setUploading(true);
        setError(null);
        try {
            const uploadResult = await uploadBrandAsset(file);
            const result = await updateBrandingDraft(draft.draft_id, {
                logo_mark_url: uploadResult.url,
                save_to_brand_guide: true,
            });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to upload logo mark'));
        } finally {
            setUploading(false);
        }
    };

    const handlePickExisting = async (url: string) => {
        if (!draft) return;
        setSaving(true);
        setError(null);
        try {
            const result = await updateBrandingDraft(draft.draft_id, {
                logo_mark_url: url,
                save_to_brand_guide: false,
            });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to select image'));
        } finally {
            setSaving(false);
        }
    };

    const handleRegenerate = async () => {
        setRegenerating(true);
        setError(null);
        try {
            const result = await createBrandingDraft(accountId);
            onDraftUpdated(result);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to regenerate images'));
        } finally {
            setRegenerating(false);
        }
    };

    // Scoped cover-only regenerate (Gemini call — costs quota). Guidance is
    // typed then explicitly submitted here, never generated live-as-you-type
    // (image generation costs quota/money, per the spec).
    const handleRegenerateCover = async () => {
        if (!draft) return;
        setRegeneratingCover(true);
        setError(null);
        try {
            const result = await regenerateBrandingCover(draft.draft_id, {
                banner_prompt_guidance: coverGuidance,
            });
            onDraftUpdated({ ...draftData!, draft: result.draft, quota_reason: result.quota_reason });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to regenerate the cover photo'));
        } finally {
            setRegeneratingCover(false);
        }
    };

    // Overlay toggle/variant change — pure local compositing, no AI call, so
    // this stays snappy and never touches image-generation quota.
    const handleToggleCoverOverlay = async (checked: boolean) => {
        if (!draft) return;
        setApplyingOverlay(true);
        setError(null);
        try {
            const result = await updateBrandingDraft(draft.draft_id, { cover_logo_overlay: checked });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to update the logo overlay'));
        } finally {
            setApplyingOverlay(false);
        }
    };

    const handleChangeCoverVariant = async (variant: NonNullable<BrandingDraft['cover_logo_variant']>) => {
        if (!draft) return;
        setApplyingOverlay(true);
        setError(null);
        try {
            const result = await updateBrandingDraft(draft.draft_id, { cover_logo_variant: variant });
            onDraftUpdated({ ...draftData!, draft: result.draft });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to update the logo overlay'));
        } finally {
            setApplyingOverlay(false);
        }
    };

    const handleDiscard = async () => {
        if (!draft) return;
        if (!window.confirm('Discard this branding draft? You can generate a new one at any time.')) return;
        setDiscarding(true);
        setError(null);
        try {
            await discardBrandingDraft(draft.draft_id);
            onClose();
        } catch (e: any) {
            setError(getApiError(e, 'Failed to discard draft'));
        } finally {
            setDiscarding(false);
        }
    };

    const toggleField = (key: PushField) => {
        setSelectedFields((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handlePush = async () => {
        if (!draft) return;
        // Never send a field whose checkbox is shown disabled — the single
        // isFieldDisabled chokepoint, shared with the checkbox render.
        const enabledDefs = PUSH_FIELD_DEFS.filter((f) => selectedFields.has(f.key) && !isFieldDisabled(f.key));
        const fields = enabledDefs.map((f) => f.key);
        if (fields.length === 0) {
            setError('Select at least one field to push.');
            return;
        }
        const fieldLabels = enabledDefs.map((f) => f.label).join(', ');
        let confirmMessage = `This will write live to the Facebook Page "${accountLabel}":\n\n${fieldLabels}\n\n`
            + 'AIME cannot restore the current profile picture or cover photo once replaced.';
        if (fields.includes('location') && addressSummary) {
            confirmMessage += `\n\nAddress will be set to:\n${addressSummary}`;
        }
        if (fields.includes('hours') && hoursSummaryLines.length > 0) {
            confirmMessage += `\n\nBusiness hours will be set to:\n${hoursSummaryLines.join('\n')}`;
        }
        confirmMessage += ' Continue?';
        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) return;

        setPushing(true);
        setError(null);
        try {
            const result = await pushBrandingDraft(draft.draft_id, fields);
            onDraftUpdated({
                ...draftData!,
                draft: { ...draft, status: result.status, field_results: result.field_results },
            });
        } catch (e: any) {
            const data = e?.response?.data;
            const detailObj = data?.detail;
            if (detailObj && typeof detailObj === 'object') {
                if (detailObj.field_results) {
                    onDraftUpdated({
                        ...draftData!,
                        draft: { ...draft, status: 'failed', field_results: detailObj.field_results },
                    });
                }
                setError(detailObj.detail || 'Failed to push to Facebook');
            } else {
                setError(getApiError(e, 'Failed to push to Facebook'));
            }
        } finally {
            setPushing(false);
        }
    };

    const handleRefreshContact = async () => {
        if (!draft) return;
        setRefreshingContact(true);
        setError(null);
        try {
            const result = await refreshBrandingDraftContact(draft.draft_id);
            onDraftUpdated(result);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to refresh contact details'));
        } finally {
            setRefreshingContact(false);
        }
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleUploadLogoMark(file);
        e.target.value = '';
    };

    if (!isOpen) return null;

    const capability = draftData?.capability;
    const brandAssets = draftData?.brand_assets;
    const projectContact = draftData?.project_contact;
    const pageCurrent = draftData?.page_current;
    const isEditable = draft?.status === 'draft';

    // page_current.location comes straight off the Graph API's Page.location
    // shape (street/city/state/zip/country, possibly with lat/lng) — format
    // it the same way addressSummary formats the AIME-side structured
    // address, so a user can visually compare "what's live" vs "what we'd
    // push" before ticking Replace.
    const currentLocationSummary = pageCurrent?.location
        ? [pageCurrent.location.street, pageCurrent.location.city, pageCurrent.location.state,
            pageCurrent.location.zip, pageCurrent.location.country].filter(Boolean).join(', ') || null
        : null;
    const currentEmailsSummary = pageCurrent?.emails && pageCurrent.emails.length > 0
        ? pageCurrent.emails.join(', ')
        : null;

    // Address, phone, email and website all render from the draft's own
    // SNAPSHOT (draft.contact_*), never from live project_contact — the F2
    // fix. project_contact remains available only for the stale-snapshot
    // banner below and the "Edit in Project Settings" link.
    const addressStructured = draft?.contact_address || null;
    const addressPushable = !!draft?.contact_address_pushable;
    const addressMessage: string | null = draft?.contact_address_block_reason
        ? (ADDRESS_BLOCKED_MESSAGE[draft.contact_address_block_reason]
            || 'This address can\'t be pushed to Facebook yet — check it in Project Settings.')
        : null;
    const addressSummary = addressStructured
        ? [addressStructured.street_address, addressStructured.city, addressStructured.state,
            addressStructured.zip, addressStructured.country].filter(Boolean).join(', ')
        : null;

    // Business hours — same snapshot-not-live pattern as address above.
    const hoursStructured = draft?.contact_hours || null;
    const hoursPushable = !!draft?.contact_hours_pushable;
    const hoursMessage: string | null = draft?.contact_hours_block_reason
        ? (draft.contact_hours_block_reason === 'skipped_no_source' && !hoursStructured
            ? null // absent schedule: the row's own "Not set — skipped" covers it, no extra line
            : HOURS_BLOCKED_MESSAGE[draft.contact_hours_block_reason] || null)
        : null;
    const hoursSummaryLines = formatHoursSummary(hoursStructured);
    const currentHoursSummaryLines = formatGraphHoursSummary(pageCurrent?.hours || null);

    // A single chokepoint for "is this field's checkbox disabled" — used by
    // both the checkbox render and handlePush, so a request can never
    // contain a field whose checkbox is shown disabled (previously the
    // render masked it via `checked={!disabled && ...}` while handlePush
    // read the raw selectedFields set).
    const isFieldDisabled = (key: PushField): boolean => (
        (key === 'location' && !addressPushable) || (key === 'hours' && !hoursPushable)
    );

    const snapshotChanged = draftData?.contact_snapshot_changed || [];
    const SNAPSHOT_FIELD_LABEL: Record<string, string> = {
        phone: 'Phone', emails: 'Contact email', website: 'Website', location: 'Address', hours: 'Business hours',
    };

    return (
        <GlassModal
            isOpen={isOpen}
            onClose={onClose}
            title={`Facebook Page Branding — ${accountLabel}`}
            size="lg"
            footer={
                <div className="fb-branding-footer">
                    <GlassButton variant="secondary" onClick={onClose}>
                        Close
                    </GlassButton>
                    {draft && isEditable && (
                        <GlassButton
                            variant="danger"
                            icon={<Trash2 size={14} />}
                            onClick={handleDiscard}
                            loading={discarding}
                        >
                            Discard
                        </GlassButton>
                    )}
                    {draft && isEditable ? (
                        <GlassButton
                            variant="primary"
                            icon={<Send size={14} />}
                            onClick={handlePush}
                            loading={pushing}
                            disabled={selectedFields.size === 0 || capability?.can_push === false}
                        >
                            Push to Facebook
                        </GlassButton>
                    ) : (
                        <span
                            title={
                                draft
                                    ? draft.status === 'pending_approval'
                                        ? 'This draft is queued for approval — review it in the Autonomy panel'
                                        : `This draft is '${draft.status}' and can no longer be pushed`
                                    : 'Generate a draft first'
                            }
                        >
                            <GlassButton variant="primary" disabled>
                                Push to Facebook
                            </GlassButton>
                        </span>
                    )}
                </div>
            }
        >
            {capability && <BrandingCapabilityBanner capability={capability} onReconnect={onReconnect} />}
            {error && <div className="branding-account-error">{error}</div>}

            {!draft && (
                <div className="fb-branding-empty-state">
                    <p>No branding draft yet.</p>
                </div>
            )}

            {draft && (
                <div className="fb-branding-body">
                    <div className="fb-branding-status-row">
                        <GlassBadge variant={STATUS_BADGE_VARIANT[draft.status] || 'default'}>
                            Status: {draft.status}
                        </GlassBadge>
                        <GlassButton
                            variant="secondary"
                            size="sm"
                            icon={<RefreshCw size={13} />}
                            onClick={handleRegenerate}
                            loading={regenerating}
                        >
                            Regenerate Images
                        </GlassButton>
                    </div>

                    {/* Images */}
                    <div className="fb-branding-image-grid">
                        <div className="fb-branding-image-tile-wrap">
                            <div className="branding-preview-section-title">Profile picture</div>
                            <div className="fb-branding-image-tile fb-branding-image-tile-profile">
                                {draft.profile_image_url ? (
                                    <img
                                        src={draft.profile_image_url}
                                        alt="Profile"
                                        style={{
                                            objectFit: draft.profile_image_source === 'gemini' ? 'cover' : 'contain',
                                        }}
                                    />
                                ) : (
                                    <div className="fb-branding-image-placeholder">No image</div>
                                )}
                            </div>
                            <div className="fb-branding-provenance">
                                {SOURCE_LABEL[draft.profile_image_source] || draft.profile_image_source}
                            </div>
                            {isEditable && (
                                <div className="fb-branding-image-actions">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={onFileChange}
                                    />
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        icon={<Upload size={13} />}
                                        onClick={() => fileInputRef.current?.click()}
                                        loading={uploading}
                                    >
                                        Upload logo mark
                                    </GlassButton>
                                    {brandAssets?.logo_url && (
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handlePickExisting(brandAssets.logo_url!)}
                                        >
                                            Use my full logo
                                        </GlassButton>
                                    )}
                                    {brandAssets?.favicon_url && (
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handlePickExisting(brandAssets.favicon_url!)}
                                        >
                                            Use my favicon
                                        </GlassButton>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="fb-branding-image-tile-wrap">
                            <div className="branding-preview-section-title">Cover photo</div>
                            <div className="fb-branding-image-tile fb-branding-image-tile-cover">
                                {draft.banner_image_url ? (
                                    <img src={draft.banner_image_url} alt="Cover" />
                                ) : (
                                    <div className="fb-branding-image-placeholder">No image</div>
                                )}
                            </div>
                            <div className="fb-branding-provenance">AI-generated</div>

                            {isEditable && (
                                <div className="fb-branding-cover-controls">
                                    <textarea
                                        className="fb-branding-textarea fb-branding-cover-guidance"
                                        rows={2}
                                        maxLength={400}
                                        placeholder='Steer the cover photo, e.g. "more colorful", "include our product", "less corporate, more playful"'
                                        value={coverGuidance}
                                        onChange={(e) => setCoverGuidance(e.target.value)}
                                    />
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        icon={<RefreshCw size={13} />}
                                        onClick={handleRegenerateCover}
                                        loading={regeneratingCover}
                                    >
                                        Regenerate Cover
                                    </GlassButton>

                                    <label className="fb-branding-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={!!draft.cover_logo_overlay}
                                            disabled={applyingOverlay || !(draft.banner_base_image_url || draft.banner_image_url)}
                                            onChange={(e) => handleToggleCoverOverlay(e.target.checked)}
                                        />
                                        Add logo overlay
                                    </label>
                                    {draft.cover_logo_overlay && (
                                        <select
                                            className="fb-branding-logo-variant-select"
                                            value={draft.cover_logo_variant || 'logo_mark'}
                                            disabled={applyingOverlay}
                                            onChange={(e) => handleChangeCoverVariant(
                                                e.target.value as NonNullable<BrandingDraft['cover_logo_variant']>,
                                            )}
                                        >
                                            {COVER_LOGO_VARIANT_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {draftData?.quota_reason && (
                        <div className="fb-branding-source-warning">
                            Image generation was limited: {draftData.quota_reason}
                        </div>
                    )}

                    {/* About */}
                    <div className="fb-branding-field-row">
                        <div className="branding-preview-section-title">About (short)</div>
                        <div className="fb-branding-provenance">
                            {PROVENANCE_LABEL[draft.bio_source] || draft.bio_source}
                            {draft.bio_source_field ? ` → ${draft.bio_source_field}` : ''}
                        </div>
                        <textarea
                            className="fb-branding-textarea"
                            rows={2}
                            maxLength={300}
                            value={bioText}
                            disabled={!isEditable}
                            onChange={(e) => setBioText(e.target.value)}
                        />
                        <div className="fb-branding-char-count">{bioText.length}/100 (Facebook truncates About)</div>
                        {draft.bio_source === 'existing_brand_data' && (
                            <GlassButton variant="secondary" size="sm" onClick={handleUseAiBio}>
                                Use AI-written bio instead
                            </GlassButton>
                        )}
                        {draft.bio_source === 'fallback' && (
                            <div className="fb-branding-result-fail">
                                This bio is generic fallback copy — edit it before it can be pushed.
                            </div>
                        )}
                    </div>

                    <div className="fb-branding-field-row">
                        <div className="branding-preview-section-title">Description (full)</div>
                        <div className="fb-branding-provenance">
                            {PROVENANCE_LABEL[draft.about_long_source || 'gemini']}
                            {draft.about_long_source_field ? ` → ${draft.about_long_source_field}` : ''}
                        </div>
                        <textarea
                            className="fb-branding-textarea"
                            rows={4}
                            value={aboutLongText}
                            disabled={!isEditable}
                            onChange={(e) => setAboutLongText(e.target.value)}
                        />
                        {pageCurrent && (
                            <div className="fb-branding-current-value">
                                Currently on Facebook: {pageCurrent.description || pageCurrent.about || <em>Not set</em>}
                            </div>
                        )}
                        {isEditable && (
                            <label className="fb-branding-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={draft.replace_page_description}
                                    onChange={(e) => handleToggleReplaceDescription(e.target.checked)}
                                />
                                Replace the Page's existing description
                            </label>
                        )}
                    </div>

                    {isEditable && (
                        <GlassButton variant="secondary" size="sm" onClick={handleSaveBio} loading={saving}>
                            Save Bio
                        </GlassButton>
                    )}

                    {/* Contact details */}
                    <div className="fb-branding-field-row">
                        <div className="branding-preview-section-title">Contact details</div>
                        {projectContact?.project_id && (
                            <a
                                href={`/projects/${projectContact.project_id}/settings`}
                                target="_blank"
                                rel="noreferrer"
                                className="fb-branding-edit-link"
                            >
                                Edit in Project Settings
                            </a>
                        )}
                        {draft.contact_source === 'none' ? (
                            <div className="fb-branding-contact-deferred">
                                No contact details on this project yet — add them in Project Settings and regenerate.
                            </div>
                        ) : (
                            <>
                                {snapshotChanged.length > 0 && isEditable && (
                                    <div className="fb-branding-source-warning fb-branding-stale-banner">
                                        <div className="fb-branding-stale-banner-text">
                                            <Info size={13} />
                                            <span>
                                                Project Settings changed since this draft was made (
                                                {snapshotChanged.map((f) => SNAPSHOT_FIELD_LABEL[f] || f).join(', ')}
                                                ). This draft will still send the old values.
                                            </span>
                                        </div>
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            icon={<RefreshCw size={13} />}
                                            onClick={handleRefreshContact}
                                            loading={refreshingContact}
                                        >
                                            Use latest details
                                        </GlassButton>
                                    </div>
                                )}
                                <div className="fb-branding-contact-row">
                                    <span>Phone</span>
                                    <span>{draft.contact_phone || <em>Not set — skipped</em>}</span>
                                </div>
                                {pageCurrent && (
                                    <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                        <span>Currently on Facebook</span>
                                        <span>{pageCurrent.phone || <em>Not set</em>}</span>
                                    </div>
                                )}
                                <div className="fb-branding-contact-row">
                                    <span>Contact email</span>
                                    <span>{draft.contact_email || <em>Not set — skipped</em>}</span>
                                </div>
                                {pageCurrent && (
                                    <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                        <span>Currently on Facebook</span>
                                        <span>{currentEmailsSummary || <em>Not set</em>}</span>
                                    </div>
                                )}
                                {draft.contact_email && (
                                    <label className="fb-branding-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={draft.replace_page_emails}
                                            onChange={(e) => handleToggleReplaceEmails(e.target.checked)}
                                        />
                                        Replace the Page's existing email(s)
                                    </label>
                                )}
                                <div className={`fb-branding-contact-row ${addressPushable ? '' : 'fb-branding-contact-deferred'}`}>
                                    <span>Address</span>
                                    <span>
                                        {addressSummary || <em>Not set — skipped</em>}
                                    </span>
                                </div>
                                {pageCurrent && (
                                    <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                        <span>Currently on Facebook</span>
                                        <span>{currentLocationSummary || <em>Not set</em>}</span>
                                    </div>
                                )}
                                {addressMessage && (
                                    <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                        <span />
                                        <span>
                                            <Info size={12} /> {addressMessage}
                                        </span>
                                    </div>
                                )}
                                {addressPushable && (
                                    <label className="fb-branding-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={draft.replace_page_location}
                                            onChange={(e) => handleToggleReplaceLocation(e.target.checked)}
                                        />
                                        Replace the Page's existing address
                                    </label>
                                )}
                                <div className={`fb-branding-contact-row ${hoursPushable ? '' : 'fb-branding-contact-deferred'}`}>
                                    <span>Business hours</span>
                                    <span>
                                        {hoursSummaryLines.length > 0 ? (
                                            <span className="fb-branding-hours-lines">
                                                {hoursSummaryLines.map((line) => <span key={line}>{line}</span>)}
                                            </span>
                                        ) : (
                                            <em>Not set — skipped</em>
                                        )}
                                    </span>
                                </div>
                                {pageCurrent && (
                                    <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                        <span>Currently on Facebook</span>
                                        <span>
                                            {currentHoursSummaryLines.length > 0 ? (
                                                <span className="fb-branding-hours-lines">
                                                    {currentHoursSummaryLines.map((line) => <span key={line}>{line}</span>)}
                                                </span>
                                            ) : (
                                                <em>Not set</em>
                                            )}
                                        </span>
                                    </div>
                                )}
                                {hoursMessage && (
                                    <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                        <span />
                                        <span>
                                            <Info size={12} /> {hoursMessage}
                                        </span>
                                    </div>
                                )}
                                {hoursPushable && currentHoursSummaryLines.length > 0 && (
                                    <label className="fb-branding-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={draft.replace_page_hours}
                                            onChange={(e) => handleToggleReplaceHours(e.target.checked)}
                                        />
                                        Replace the Page's existing hours
                                    </label>
                                )}
                                <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                    <span />
                                    <span>
                                        Landing-page text: {projectContact?.office_hours || <em>Not set</em>}
                                    </span>
                                </div>
                                <div className="fb-branding-contact-row">
                                    <span>Website</span>
                                    <span>{draft.contact_website || <em>Not set — skipped</em>}</span>
                                </div>
                                {pageCurrent && (
                                    <div className="fb-branding-contact-row fb-branding-contact-deferred">
                                        <span>Currently on Facebook</span>
                                        <span>{pageCurrent.website || <em>Not set</em>}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Field selection for the live push (Phase 3) */}
                    {isEditable && (
                        <div className="fb-branding-field-row">
                            <div className="branding-preview-section-title">Push to Facebook</div>
                            <div className="fb-branding-push-fields">
                                {PUSH_FIELD_DEFS.map((f) => {
                                    const disabled = isFieldDisabled(f.key);
                                    return (
                                        <label key={f.key} className="fb-branding-checkbox-row">
                                            <input
                                                type="checkbox"
                                                checked={!disabled && selectedFields.has(f.key)}
                                                disabled={disabled}
                                                onChange={() => toggleField(f.key)}
                                            />
                                            {f.label}
                                        </label>
                                    );
                                })}
                            </div>
                            {capability && capability.can_push === false && (
                                <div className="fb-branding-result-fail">
                                    This Page can't be pushed to right now — reconnect Facebook above.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Per-field results, if any (from a live push) */}
                    {draft.field_results && (
                        <div className="fb-branding-field-row">
                            <div className="branding-preview-section-title">Last push result</div>
                            {PUSH_FIELD_DEFS
                                .filter((f) => draft.field_results && draft.field_results[f.key])
                                .map((f) => {
                                    const r = draft.field_results![f.key];
                                    const described = describeFieldResult(f.key, f.label, r);
                                    const toneClass = described.tone === 'ok'
                                        ? 'fb-branding-result-ok'
                                        : described.tone === 'skipped'
                                            ? 'fb-branding-result-skipped'
                                            : 'fb-branding-result-fail';
                                    return (
                                        <div key={f.key} className={toneClass}>
                                            {described.text}
                                            {described.detail && (
                                                <div className="fb-branding-result-detail">{described.detail}</div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            )}
        </GlassModal>
    );
};

export default FacebookBrandingModal;
